import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { Combobox } from '@headlessui/react';
import { VariantProps, cva, cx } from 'class-variance-authority';

export type ComboBoxOption = {
  value: string;
  label: string;
  id: string;
  indent?: boolean;
  isParent?: boolean;
};

type Props = {
  isDisabled?: boolean;
  placeholder?: string;
  options: ComboBoxOption[];
  allowEmpty?: boolean;
  onChange: (option: ComboBoxOption) => void;
  value?: ComboBoxOption['value'];
  autoComplete?: string;
} & VariantProps<typeof comboBoxStyles>;

export const comboBoxStyles = cva(
  'w-full rounded-[1rem] trundate leading-[2.25rem] text-[1.5rem] py-[1rem] px-[1rem] md:px-[2.4rem] bg-brand-darker-white focus:bg-brand-lighter-gray border-none focus:ring-0',
  {
    variants: {
      error: {
        true: 'outline outline-2 outline-brand-red focus:outline-brand-red',
        false:
          'outline outline-2 outline-transparent focus:outline-brand-secondary',
      },
      disabled: {
        true: 'opacity-50',
        false: 'bg-brand-darker-white',
      },
      defaultVariants: {
        error: 'false',
      },
    },
  }
);

const ComboBox = ({
  isDisabled = false,
  placeholder = "",
  options,
  allowEmpty = true,
  error,
  onChange,
  value,
  autoComplete,
}: Props) => {
  const [query, setQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState<ComboBoxOption | null>(
    options.find((o) => o.value === value) ?? null
  );
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Sync internal state when external value prop changes
  useEffect(() => {
    const matchingOption = options.find((o) => o.value === value) ?? null;
    if (matchingOption?.value !== selectedOption?.value) {
      setSelectedOption(matchingOption);
    }
  }, [value, options]);
  if (
    allowEmpty &&
    !!selectedOption?.value &&
    !options.some((o) => o.id === 'empty')
  ) {
    options.unshift({ id: 'empty', label: '', value: '' });
  }

  useEffect(() => {
    if (selectedOption !== null) {
      onChange(selectedOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);
  const filteredOptions = options.filter((options) => {
    return options.label.toLowerCase().includes(query.toLowerCase());
  });

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    // Detect autofill, match and select an option
    if (event.target.matches('*:-webkit-autofill')) {
      const foundOption = options.find((o) => o.label === val);
      if (foundOption) {
        setSelectedOption(foundOption);
        setTimeout(() => {
          buttonRef.current?.click();
        }, 0);
        return;
      }
    }
    setQuery(val);
  };

  return (
    <Combobox 
      disabled={isDisabled}
      as="div" 
      value={selectedOption} 
      onChange={setSelectedOption}
    >
      <div className="relative">
        <Combobox.Input
          autoComplete={autoComplete}
          placeholder={placeholder}
          onChange={onInputChange}
          displayValue={(v: ComboBoxOption) => v?.label ?? ''}
          className={comboBoxStyles({ error, disabled: isDisabled })}
        />
        <Combobox.Button
          ref={buttonRef}
          className="absolute inset-y-0 right-[0.8rem] md:right-[2rem] flex items-center rounded-r-[1rem] pr-2 focus:outline-hidden"
        >
          <ChevronDownIcon
            className="h-8 w-8 text-brand-gray"
            aria-hidden="true"
          />
        </Combobox.Button>

        {filteredOptions?.length > 0 && (
          <Combobox.Options className="absolute z-overlay mt-2 max-h-96 w-full min-w-[20rem] overflow-auto rounded-[.8rem] bg-brand-white py-1 text-[1.4rem] shadow-xs ring-1 ring-brand-black/5 focus:outline-hidden">
            {filteredOptions.map((option) => (
              <Combobox.Option
                key={option.id}
                value={option}
                disabled={option.id === "separator" ? true : false}
                className={({ active }) =>
                  cx(
                    'relative flex h-10 select-none items-center pr-4',
                    active ? 'bg-brand-secondary-light' : '',
                    option.indent ? 'pl-[1.3rem]' : '',
                    option.isParent ? 'text-semibold' : ''
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={cx(
                        'block truncate',
                        selected ? 'font-medium' : ''
                      )}
                    >
                      {option.label}
                    </span>

                    {selected && (
                      <span
                        className={cx(
                          'absolute inset-y-0 left-0 flex items-center pl-1.5 text-brand-secondary'
                        )}
                      >
                        <CheckIcon className="h-6 w-6" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};

export default ComboBox;
